// --- React ---
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Services ---
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import FormInput from "../../../components/form-input/FormInput";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import ButtonGoback from "../../../components/button/buttonGoback";

// --- Models ---
import { RoleItem } from "../../../models/types/role";
import { roleSchema } from "../../../models/schema/role";
import rolesSvc from "../../../services/api/roles";

// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { LoaderCircle } from "lucide-react";
import { UrlCollection } from "../../../common/url-collection";
import { useTranslation } from "react-i18next";

interface FormProps {
  isCreate: boolean;
  data: RoleItem | null;
}

export default function ParameterCreateUpdate({ isCreate, data }: FormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const schema = roleSchema;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      code: "",
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        ...omitMetaFields(data),
      });
    }
  }, [data]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = isCreate ? await rolesSvc.createRole(values) : await rolesSvc.updateRole(values);
      if (res && res.content.status) {
        unlock();
        notify(t("common.success"), isCreate ? t("roles.createSuccess") : t("roles.updateSuccess"), "success");
        setLoading(false);
        navigate(UrlCollection.Roles);
      }
    } catch (e: any) {
      notify(t("common.error"), t("common.errorOccurred"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  return (
    <>
      <ModalConfirm
        isOpen={showModal}
        onClose={cancelLeave}
        title={confirmContent.title}
        content={confirmContent.content}
        titleComfirm={t("common.leavePage")}
        action={confirmLeave}
      />
      <div className="w-full h-full">
        <ButtonGoback />
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow mt-3">
          <h2 className="text-2xl font-bold mb-6 text-center">{isCreate ? t("roles.createRole") : t("roles.updateRole")}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4">
              <FormInput
                label={t("roles.name")}
                name="name"
                type="text"
                register={register("name")}
                error={errors.name}
                required
              />
              <FormInput
                disabled={!isCreate}
                label={t("roles.code")}
                name="code"
                type="text"
                register={register("code")}
                error={errors.code}
                required
              />
            </div>
            <button
              disabled={!isDirty}
              type="submit"
              className="btn_submit w-full mt-4"
            >
              <div className="flex items-center justify-center gap-2">
                {loading ? <LoaderCircle /> : ""}
                {isCreate ? t("roles.create") : t("roles.update")}
              </div>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}